import type { Directive, DirectiveBinding } from 'vue'
import './waves.css'

const context = '@@wavesContext'

interface WavesElement extends HTMLElement {
  [context]?: {
    removeHandle: (e: MouseEvent) => void
  }
}

interface WavesOptions {
  ele?: HTMLElement
  type?: 'hit' | 'center'
  color?: string
}

function handleClick(el: WavesElement, binding: DirectiveBinding) {
  function handle(e: MouseEvent) {
    const customOpts: WavesOptions = Object.assign({}, binding.value)
    const opts: Required<WavesOptions> = Object.assign(
      {
        ele: el,
        type: 'hit' as const,
        color: 'rgba(0, 0, 0, 0.15)'
      },
      customOpts
    )

    const target = opts.ele
    if (target) {
      target.style.position = 'relative'
      target.style.overflow = 'hidden'
      const rect = target.getBoundingClientRect()
      let ripple = target.querySelector('.waves-ripple') as HTMLSpanElement | null

      if (!ripple) {
        ripple = document.createElement('span')
        ripple.className = 'waves-ripple'
        ripple.style.height = ripple.style.width = Math.max(rect.width, rect.height) + 'px'
        target.appendChild(ripple)
      } else {
        ripple.className = 'waves-ripple'
      }

      switch (opts.type) {
        case 'center':
          ripple.style.top = rect.height / 2 - ripple.offsetHeight / 2 + 'px'
          ripple.style.left = rect.width / 2 - ripple.offsetWidth / 2 + 'px'
          break
        default:
          ripple.style.top =
            (e.pageY - rect.top - ripple.offsetHeight / 2 - (document.documentElement.scrollTop || document.body.scrollTop)) + 'px'
          ripple.style.left =
            (e.pageX - rect.left - ripple.offsetWidth / 2 - (document.documentElement.scrollLeft || document.body.scrollLeft)) + 'px'
      }

      ripple.style.backgroundColor = opts.color
      ripple.className = 'waves-ripple z-active'
      return false
    }
  }

  if (!el[context]) {
    el[context] = {
      removeHandle: handle
    }
  } else {
    el[context].removeHandle = handle
  }

  return handle
}

const wavesDirective: Directive = {
  beforeMount(el: WavesElement, binding: DirectiveBinding) {
    el.addEventListener('click', handleClick(el, binding), false)
  },

  updated(el: WavesElement, binding: DirectiveBinding) {
    if (el[context]) {
      el.removeEventListener('click', el[context].removeHandle, false)
    }
    el.addEventListener('click', handleClick(el, binding), false)
  },

  unmounted(el: WavesElement) {
    if (el[context]) {
      el.removeEventListener('click', el[context].removeHandle, false)
      delete el[context]
    }
  }
}

export default wavesDirective
